import { z } from "zod";
import { dayjs, type Dayjs } from "@marekl/adminer/datetime";
import type { MembershipRole, Person, PersonPayload } from "../../../lib/queries/memberships";

// MEMBERSHIP_KIND_OPTIONS drives the „Typ členství" select. Labels come from
// memberState.ts so the wording matches the badges.
export const MEMBERSHIP_KIND_OPTIONS = ["registered", "guest"] as const;

export const ROLE_LABELS: Record<MembershipRole, string> = {
  athlete: "Soutěžící",
  coach: "Trenér",
  assistant: "Asistent",
};

export const ROLE_OPTIONS: MembershipRole[] = ["athlete", "coach", "assistant"];
// Vedoucí can only be additionally an athlete or coach — not an assistant.
export const LEADER_ROLE_OPTIONS: MembershipRole[] = ["athlete", "coach"];

/**
 * The four fields the backend treats as a Person's identity. Everything else
 * (address, telefon) is contact data a leader keeps current year-round.
 */
export const IDENTITY_FIELDS = ["first_name", "last_name", "birth_date", "birth_number"] as const;
export type IdentityField = (typeof IDENTITY_FIELDS)[number];

/**
 * The string fields the ČNSMS data set requires of a registered member on top
 * of the identity (mirrors validatePersonFull in internal/service/membership).
 * The one list feeds both the form schema below and the draft grid's
 * no-blanking / incomplete-row checks, so the dialog and the grid cannot
 * disagree about what a registration needs.
 */
export const FULL_DATA_FIELDS = ["birth_number", "street", "city", "postal_code"] as const;

/**
 * The part of FULL_DATA_FIELDS a registration actually REQUIRES. The rodné
 * číslo is collected and only the members' office may erase one once filled,
 * in the registry or the roster dialog (it stays in the list above, so the
 * draft grid keeps refusing the clear for everyone), but for now a member may be
 * registered without one: an empty number is a warning, never a refusal
 * (validatePersonFull says the same). The team leader is the exception,
 * enforced server-side (ErrLeaderBirthNumberMissing).
 */
export const REQUIRED_DATA_FIELDS = ["street", "city", "postal_code"] as const;

/** The warning shown under an empty rodné číslo of a člen. */
export const BIRTH_NUMBER_MISSING_HINT = "Chybí rodné číslo.";

/**
 * Why a person's identity cannot be edited here, mirroring the backend:
 *
 *   - `leader`   — the team's current leader, locked year-round
 *                  (ErrLeaderIdentityLocked).
 *   - `closed`   — registration is over; the whole identity is frozen, gaps
 *                  included (ErrPersonIdentityLocked).
 *   - `filled`   — the ordinary case with the window open: a field that
 *                  already holds a value may not be overwritten, an empty one
 *                  may still be completed (ErrPersonIdentityImmutable).
 *   - `null`     — no lock: a members_admin, or a nečlen, who is not on the
 *                  soupiska the lock exists to freeze.
 */
export type IdentityLock = "leader" | "closed" | "filled" | null;

const required = (message = "Povinné pole") => z.string().trim().min(1, message);

// baseMemberSchema carries the field shape shared by every member form.
// The member / leader variants below differ ONLY in role validation, so
// they keep identical input and output types — that is what lets the one
// EditMemberModal serve both without casting its form generics.
const baseMemberSchema = z.object({
  person: z.object({
    first_name: required("Zadejte jméno"),
    last_name: required("Zadejte příjmení"),
    country_code: required().length(2, "Použijte dvoupísmenný ISO kód"),
    birth_date: z.custom<Dayjs | null>((v) => v !== null && v !== undefined, {
      message: "Zadejte datum narození",
    }),
    // Rodné číslo and address are required only for „Člen" — see
    // requireFullDataForMembers below. A „Nečlen" is a one-off entrant for a
    // pohárová soutěž and we have no reason to hold their address.
    birth_number: z.string().trim().optional().default(""),
    street: z.string().trim().optional().default(""),
    city: z.string().trim().optional().default(""),
    postal_code: z.string().trim().optional().default(""),
    phone: z.string().trim().optional().default(""),
  }),
  roles: z.array(z.enum(["coach", "assistant", "athlete"])),
  // „Člen" = a real season registration; „Nečlen" = a guest for pohárové
  // soutěže; „Zahraniční člen" = somebody on a team outside ČNSMS. Mirrors
  // TeamMembership.Kind, including the rule that only a registration
  // demands the full data set below.
  membership_kind: z.enum(["registered", "guest", "foreign"]),
});

// requireFullDataForMembers mirrors validatePersonFull in
// internal/service/membership: registering someone into a season demands the
// ČNSMS address, creating a guest does not. Applied as a superRefine so the
// fields stay plain strings in the form's type.
const requireFullDataForMembers = (
  v: z.infer<typeof baseMemberSchema>,
  ctx: z.RefinementCtx,
) => {
  if (v.membership_kind !== "registered") return;
  for (const field of REQUIRED_DATA_FIELDS) {
    if (!String(v.person[field] ?? "").trim()) {
      ctx.addIssue({ code: "custom", path: ["person", field], message: "Povinné pole" });
    }
  }
};

export type MemberFormValues = z.input<typeof baseMemberSchema>;
export type MemberFormOutput = z.output<typeof baseMemberSchema>;

// Ordinary member: at least one role — a roster row with no role has no
// meaning.
export const memberSchema = baseMemberSchema.superRefine((v, ctx) => {
  if (v.roles.length === 0) {
    ctx.addIssue({ code: "custom", path: ["roles"], message: "Vyberte alespoň jednu roli" });
  }
  requireFullDataForMembers(v, ctx);
});

// Team leader: an empty set is valid and means „pouze vedoucí" — they stay
// on the soupiska with no competitive role. Asistent is rejected; a vedoucí
// may additionally be only soutěžící or trenér. Mirrors validateRoles in
// internal/service/membership.
export const leaderSchema = baseMemberSchema.superRefine((v, ctx) => {
  if (v.roles.includes("assistant")) {
    ctx.addIssue({ code: "custom", path: ["roles"], message: "Vedoucí nemůže být asistent" });
  }
  requireFullDataForMembers(v, ctx);
});

// Person-only save, used by the members registry. Deliberately does NOT
// run requireFullDataForMembers: completeness is enforced at registration
// transitions only, never on an ordinary edit — the backend accepts a
// partial legacy record, and the registry is the designated repair
// surface, so blocking a phone fix on a missing rodné číslo would trap
// exactly the admin sent to fix it. What an edit must not do is decay a
// complete record, so this mirrors validateNoBlanking instead: a ČNSMS
// field that already holds a value may not be emptied. The rodné číslo is
// the exception: the registry is admin-only, and the office may clear a
// number made up for somebody who has none (validateNoBlanking's `office`).
// No role rule — roles are membership data and this schema's surfaces do not
// edit them.
export function makePersonEditSchema(original: Person) {
  return baseMemberSchema.superRefine((v, ctx) => {
    for (const field of REQUIRED_DATA_FIELDS) {
      if (original[field] && !String(v.person[field] ?? "").trim()) {
        ctx.addIssue({
          code: "custom",
          path: ["person", field],
          message: "Povinné pole",
        });
      }
    }
  });
}

// personEqual backs the dirty check both edit dialogs run before PATCHing:
// a no-op save must not produce a request (and with it an audit row).
export function personEqual(payload: PersonPayload, original: Person): boolean {
  return (
    payload.first_name === original.first_name &&
    payload.last_name === original.last_name &&
    payload.country_code === original.country_code &&
    payload.birth_date === original.birth_date &&
    payload.birth_number === original.birth_number &&
    payload.street === original.street &&
    payload.city === original.city &&
    payload.postal_code === original.postal_code &&
    payload.phone === original.phone
  );
}

// personToFormValues is the one Person → form mapping. country_code maps
// raw, no "CZ" fallback: a legacy blank means "nationality unknown", and
// prefilling a value would both stamp Czech nationality on a save the
// user never chose and read as dirty to personEqual, turning a no-op
// „Uložit změny" into a PATCH. A blank stays blank until someone picks a
// country — the schema (like the backend's validatePersonIdentity)
// refuses to save without one.
export function personToFormValues(p: Person): MemberFormValues["person"] {
  return {
    first_name: p.first_name,
    last_name: p.last_name,
    country_code: p.country_code,
    birth_date: p.birth_date ? dayjs(p.birth_date) : null,
    birth_number: p.birth_number,
    street: p.street,
    city: p.city,
    postal_code: p.postal_code,
    phone: p.phone ?? "",
  };
}

export const defaultMemberValues: MemberFormValues = {
  person: {
    first_name: "",
    last_name: "",
    country_code: "CZ",
    birth_date: null,
    birth_number: "",
    street: "",
    city: "",
    postal_code: "",
    phone: "",
  },
  roles: ["athlete"],
  membership_kind: "registered",
};
